import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import DatePicker from '@rio-cloud/rio-uikit/DatePicker';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';

import { getLocale } from '../../../../configuration';
import type { RootState } from '../../../../configuration/setup/store';
import { ContractsTableSearchContainer } from './ContractsTableSearchContainer';
import {
    contractActions,
    getFromDate,
    getHideAutoRenewingContracts,
    getHideContractsForDeletedAssets,
    getToDate,
} from './redux/contract.redux';

interface Props {
    locale: string;
    fromDate?: string;
    onFromDateChange: (fromDate?: string) => void;
    toDate?: string;
    onToDateChange: (toDate?: string) => void;
    hideContractsForDeletedAssets: boolean;
    hideAutoRenewingContracts: boolean;
    onToggleHideDeletedContracts: (state: boolean) => void;
    onToggleHideAutoRenewingContracts: (state: boolean) => void;
}

const HIDE_CONTRACTS_FOR_DELETED_ASSETS_STORAGE_KEY = 'marketplace.contract.hideContractsForDeletedAssets';
const HIDE_AUTO_RENEWING_CONTRACTS_STORAGE_KEY = 'marketplace.contract.hideAutoRenewingContracts';

const loadBooleanFromLocalStorage = (key: string) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        const storedValue = window.localStorage.getItem(key);
        return storedValue === null ? undefined : storedValue === 'true';
    } catch {
        return;
    }
};

const saveBooleanToLocalStorage = (key: string, value: boolean) => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(key, String(value));
    } catch {
        // Keep the filter usable even when localStorage is unavailable.
    }
};

export function ContractTableToolBar(props: Props) {
    const {
        locale,
        fromDate,
        onFromDateChange,
        toDate,
        onToDateChange,
        onToggleHideAutoRenewingContracts,
        onToggleHideDeletedContracts,
    } = props;

    useEffect(() => {
        const hideContractsForDeletedAssets = loadBooleanFromLocalStorage(
            HIDE_CONTRACTS_FOR_DELETED_ASSETS_STORAGE_KEY
        );
        const hideAutoRenewingContracts = loadBooleanFromLocalStorage(HIDE_AUTO_RENEWING_CONTRACTS_STORAGE_KEY);

        if (hideContractsForDeletedAssets !== undefined) {
            onToggleHideDeletedContracts(hideContractsForDeletedAssets);
        }

        if (hideAutoRenewingContracts !== undefined) {
            onToggleHideAutoRenewingContracts(hideAutoRenewingContracts);
        }
    }, [onToggleHideAutoRenewingContracts, onToggleHideDeletedContracts]);

    const toggleHideDeletedContracts = () => {
        const nextState = !props.hideContractsForDeletedAssets;
        saveBooleanToLocalStorage(HIDE_CONTRACTS_FOR_DELETED_ASSETS_STORAGE_KEY, nextState);
        onToggleHideDeletedContracts(nextState);
    };

    const toggleHideAutoRenewingContracts = () => {
        const nextState = !props.hideAutoRenewingContracts;
        saveBooleanToLocalStorage(HIDE_AUTO_RENEWING_CONTRACTS_STORAGE_KEY, nextState);
        onToggleHideAutoRenewingContracts(nextState);
    };

    return (
        <div data-onboarding-contracts-walkthrough-step='contract-view-table-toolbar'>
            <TableToolbar className={''}>
                <div className='table-toolbar-container'>
                    <div className='table-toolbar-group-left'>
                        <div className='table-toolbar-column'>
                            <label>
                                <FormattedMessage id='marketplace.contract.filter.dateFrom' />
                            </label>
                            <DatePicker
                                key={fromDate}
                                inputProps={{
                                    readOnly: true,
                                    className: 'form-control cursor-pointer bg-white',
                                    style: { borderColor: 'rgb(167, 175, 187)' },
                                }}
                                value={fromDate ? new Date(fromDate) : undefined}
                                onChange={value => {
                                    if (typeof value === 'string') {
                                        if (value === '') {
                                            onFromDateChange(undefined);
                                        }
                                    } else {
                                        onFromDateChange(value.format('YYYY-MM-DD'));
                                    }
                                }}
                                timeFormat={false}
                                minWidth={160}
                                closeOnSelect
                                clearableInput={true}
                                isValidDate={(currentDate: Date) => (toDate ? currentDate < new Date(toDate) : true)}
                                locale={locale}
                            />
                        </div>
                        <div className='table-toolbar-column'>
                            <label>
                                <FormattedMessage id='marketplace.contract.filter.dateTo' />
                            </label>
                            <DatePicker
                                key={toDate}
                                inputProps={{
                                    readOnly: true,
                                    className: 'form-control cursor-pointer bg-white',
                                    style: { borderColor: 'rgb(167, 175, 187)' },
                                }}
                                value={toDate ? new Date(toDate) : undefined}
                                onChange={value => {
                                    if (typeof value === 'string') {
                                        if (value === '') {
                                            onToDateChange(undefined);
                                        }
                                    } else {
                                        onToDateChange(value.format('YYYY-MM-DD'));
                                    }
                                }}
                                timeFormat={false}
                                minWidth={160}
                                closeOnSelect
                                clearableInput={true}
                                isValidDate={(currentDate: Date) =>
                                    fromDate ? currentDate > new Date(fromDate) : true
                                }
                                locale={locale}
                            />
                        </div>
                        <div className='table-toolbar-column'>
                            <Checkbox
                                inline
                                checked={props.hideContractsForDeletedAssets}
                                onClick={toggleHideDeletedContracts}
                            >
                                <FormattedMessage id='marketplace.hideContractsForDeletedVehicles' />
                            </Checkbox>
                        </div>
                        <div className='table-toolbar-column'>
                            <Checkbox
                                inline
                                checked={props.hideAutoRenewingContracts}
                                onClick={toggleHideAutoRenewingContracts}
                            >
                                <FormattedMessage id='marketplace.hideAutoRenewingContracts' />
                            </Checkbox>
                        </div>
                    </div>
                    <div className='table-toolbar-group-right'>
                        <div className='table-toolbar-column'>
                            <ContractsTableSearchContainer />
                        </div>
                    </div>
                </div>
            </TableToolbar>
        </div>
    );
}

const mapStateToProps = (state: RootState) => ({
    locale: getLocale(state),
    fromDate: getFromDate(state),
    toDate: getToDate(state),
    hideContractsForDeletedAssets: getHideContractsForDeletedAssets(state),
    hideAutoRenewingContracts: getHideAutoRenewingContracts(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    onFromDateChange: (fromDate?: string) => dispatch(contractActions.setFromDate(fromDate)),
    onToDateChange: (toDate?: string) => dispatch(contractActions.setToDate(toDate)),
    onToggleHideDeletedContracts: (state: boolean) => dispatch(contractActions.setHideContractsForDeletedAssets(state)),
    onToggleHideAutoRenewingContracts: (state: boolean) =>
        dispatch(contractActions.setHideAutoRenewingContracts(state)),
});

export const ContractTableToolBarContainer = connect(mapStateToProps, mapDispatchToProps)(ContractTableToolBar);
